
import { useState, useEffect } from 'react';

interface IBeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export const useInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<IBeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // 1. Detect Mobile User Agent
    const userAgent = window.navigator.userAgent.toLowerCase();
    const mobile = /iphone|ipad|ipod|android/.test(userAgent);
    setIsMobile(mobile);

    // 2. Detect iOS specifically
    const ios = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(ios);

    // 3. Detect if app is already running in standalone mode (Installed)
    const isInStandaloneMode = () => {
        return (
            window.matchMedia('(display-mode: standalone)').matches || 
            ('standalone' in window.navigator && (window.navigator as Navigator & { standalone: boolean }).standalone === true) || 
            document.referrer.includes('android-app://')
        );
    };

    setIsInstalled(isInStandaloneMode());

    // Listener for changes in display mode (e.g. user installs while on the page)
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (evt: MediaQueryListEvent) => {
        if (evt.matches) {
            setIsInstalled(true);
            setDeferredPrompt(null);
        }
    };
    mediaQuery.addEventListener('change', handleDisplayModeChange);

    // 4. Capture the Chrome/Android install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile immediately
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e as IBeforeInstallPromptEvent);
      console.log("Install prompt captured");
    };

    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  const triggerInstallPrompt = async () => {
    if (!deferredPrompt) {
      console.log("No deferred prompt available");
      return false; 
    }
    // Show the install prompt
    await deferredPrompt.prompt();
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);
    
    setDeferredPrompt(null);
    return true; 
  };

  return { 
    deferredPrompt, 
    isInstalled, 
    triggerInstallPrompt,
    isIOS,
    isMobile
  };
};
